import React from 'react';
import { Button, Col, Form, Input, InputNumber, notification, Row, Select, Space, Typography, Upload, Checkbox } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AppSelect } from '../../constants/appSelect';
import { CloseOutlined, ContainerOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { createLesson } from '../../services/lesson';
import { getCurriculums } from '../../services/curriculum';
import { getAllCategories } from '../../services/category';
import { uploadImageCDN, checkValidUpload, dataToOptionSelect } from '../../utils/utility';

const itemName = 'Bài học';
const { Text, Title } = Typography;

const CreateLesson = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { data: curriculumListRes } = useQuery({
    queryKey: ['curriculumForCreateLesson'],
    queryFn: () => getCurriculums({ page: 1, limit: 1000, status: [1] }),
  });
  const { data: allCategoriesRes } = useQuery({ queryKey: ['allCategoriesForLessonCreate'], queryFn: getAllCategories, staleTime: 1000 * 60 * 10 });
  const curriculumOptions = React.useMemo(() => {
    const curr = Array.isArray(curriculumListRes?.data) ? curriculumListRes.data : [];
    const cats = Array.isArray(allCategoriesRes?.data) ? allCategoriesRes.data : [];
    const parentMap = cats.reduce((acc, c) => { acc[c.id] = c.parent_id || 0; return acc; }, {});
    const titleMap = cats.reduce((acc, c) => { acc[c.id] = c.title || ''; return acc; }, {});
    const cache = {};
    const toPath = (id) => {
      if (cache[id]) return cache[id];
      const parts = [];
      let cur = id;
      const guard = new Set();
      while (cur && !guard.has(cur) && parts.length < 10) {
        guard.add(cur);
        parts.unshift(titleMap[cur] || '');
        cur = parentMap[cur] || 0;
      }
      cache[id] = parts.filter(Boolean).join(' — ');
      return cache[id];
    };
    const opts = curr.map((c) => {
      const catPath = toPath(c.category_id);
      return {
        value: c.id,
        label: `${catPath || '—'} - ${c.title || ''}`.trim(),
        _cat: (catPath || '').toLowerCase(),
        _title: (c.title || '').toLowerCase(),
      };
    });
    opts.sort((a, b) => a._cat.localeCompare(b._cat) || a._title.localeCompare(b._title));
    return opts;
  }, [curriculumListRes, allCategoriesRes]);

  const { mutate: mutateCreate, isPending } = useMutation({
    mutationFn: createLesson,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Thêm mới ${itemName} thành công!` });
        navigate(-1);
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...' });
    },
  });

  const handleSubmitForm = (data) => {
    const payload = {
      curriculum_id: data?.curriculum_id,
      title: data?.title,
      thumb: data?.thumb,
      description: data?.description,
      order: Number(data?.order || 0),
      status: data?.status,
      free_trial: data?.free_trial ? 1 : 0,
    };
    mutateCreate(payload);
  };

  const handleUploadThumb = async (file) => {
    const isValid = checkValidUpload(file);
    if (!isValid) return false;
    const url = await uploadImageCDN(file, () => {}, 'toancohien_lesson');
    if (url) {
      notification.success({ message: 'Tải lên thành công!' });
      const safeUrl = encodeURI(url);
      form.setFieldsValue({ thumb: safeUrl });
    }
    return false;
  };

  return (
    <Form name="create-lesson" form={form} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Thêm {itemName}</Title>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="title"
            label={<Text>Tiêu đề <span className="text-red-700">*</span></Text>}
            rules={[{ required: true, message: `Vui lòng nhập tiêu đề ${itemName}` }]}
          >
            <Input placeholder={`Nhập tiêu đề ${itemName}...`} />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="curriculum_id"
            label={<Text>Chương trình học <span className="text-red-700">*</span></Text>}
            rules={[{ required: true, message: 'Vui lòng chọn chương trình học' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Chọn chương trình học"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={curriculumOptions}
            />
          </Form.Item>
        </Col>

        {/* Ẩn ô nhập ảnh đại diện theo yêu cầu */}

        <Col xs={24}>
          <Form.Item name="description" label={<Text>Mô tả</Text>}>
            <Input.TextArea placeholder={`Nhập mô tả ${itemName}...`} autoSize={{ minRows: 3, maxRows: 8 }} maxLength={10000} showCount />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="order" label={<Text>Thứ tự</Text>} initialValue={0}>
            <InputNumber min={0} className="w-full" placeholder="Nhập thứ tự" size="large" />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="status"
            label={<Text>Trạng thái <span className="text-red-700">*</span></Text>}
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
            initialValue={1}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn trạng thái"
              options={AppSelect.STATUS}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="free_trial"
            label={<Text>Học thử</Text>}
            valuePropName="checked"
            initialValue={false}
          >
            <Checkbox>Cho phép học thử</Checkbox>
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Row justify="end">
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                  Hủy bỏ
                </Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>
                  Lưu lại
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default CreateLesson;
